import React, { useState, useEffect, useRef, useCallback } from "react";
import ThemeMaker from "../../components/thememaker";
import axios from "axios";
import { config } from "../../utils/config";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import CircularProgress from "@mui/material/CircularProgress";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { toast } from "react-toastify";

interface ExistingDoc {
  url: string;
  name: string;
  updatedAt?: string;
}

interface UploadCardProps {
  id: string;
  title: string;
  description: string;
  endpointPath: string;
  icon: any;
  file: File | null;
  existingDoc: ExistingDoc | null;
  uploadedStatus: "idle" | "uploading" | "success" | "error";
  uploadedMessage?: string;
  onFileSelect: (file: File | null) => void;
  onUploadSingle: () => void;
  acceptedFormats?: string;
}

const FileUploadCard: React.FC<UploadCardProps> = ({
  id,
  title,
  description,
  icon: IconComponent,
  file,
  existingDoc,
  uploadedStatus,
  uploadedMessage,
  onFileSelect,
  onUploadSingle,
  acceptedFormats = ".pdf",
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4 MB

  const validateAndSelectFile = (selectedFile: File) => {
    const isPdf =
      selectedFile.type === "application/pdf" ||
      selectedFile.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Only PDF files are allowed!");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error(`File size must not exceed 4 MB! Selected file is ${formatFileSize(selectedFile.size)}.`);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    onFileSelect(selectedFile);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      validateAndSelectFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      validateAndSelectFile(e.target.files[0]);
    }
  };

  const formatDocDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const isSuccess = uploadedStatus === "success";
  const isUploading = uploadedStatus === "uploading";
  const hasExisting = Boolean(existingDoc && existingDoc.url);

  return (
    <div
      className="card border-0 shadow-sm h-100"
      style={{
        borderRadius: "18px",
        backgroundColor: "#FFFFFF",
        border: isSuccess || hasExisting ? "1.5px solid #22C55E" : file ? "1.5px solid #F97316" : "1.5px solid #E2E8F0",
        transition: "all 0.2s ease-in-out",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Hidden file input */}
      <input
        type="file"
        ref={inputRef}
        onChange={handleChange}
        accept={acceptedFormats}
        style={{ display: "none" }}
        id={id}
      />

      {/* Header */}
      <div
        style={{
          padding: "16px 20px",
          backgroundColor: isSuccess || hasExisting ? "#F0FDF4" : file ? "#FFF7ED" : "#F8FAFC",
          borderBottom: "1px solid #F1F5F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "10px",
        }}
      >
        <div className="d-flex align-items-center" style={{ gap: "12px", minWidth: 0 }}>
          <div
            style={{
              width: "38px",
              height: "38px",
              borderRadius: "10px",
              backgroundColor: isSuccess || hasExisting ? "#DCFCE7" : file ? "#FFEDD5" : "#F1F5F9",
              color: isSuccess || hasExisting ? "#16A34A" : file ? "#EA580C" : "#64748B",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isSuccess || hasExisting ? <CheckCircleIcon style={{ fontSize: "22px" }} /> : <IconComponent style={{ fontSize: "22px" }} />}
          </div>
          <div style={{ minWidth: 0 }}>
            <h6 className="m-0 font-weight-bold text-truncate" style={{ color: "#0F172A", fontSize: "15px" }}>
              {title}
            </h6>
            <span style={{ fontSize: "12px", color: "#64748B" }}>{description}</span>
          </div>
        </div>

        {isSuccess ? (
          <span
            className="badge"
            style={{
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              padding: "5px 12px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            Just Uploaded
          </span>
        ) : hasExisting ? (
          <span
            className="badge"
            style={{
              backgroundColor: "#DCFCE7",
              color: "#15803D",
              padding: "5px 12px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            Active
          </span>
        ) : file ? (
          <span
            className="badge"
            style={{
              backgroundColor: "#FFEDD5",
              color: "#C2410C",
              padding: "5px 12px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            Ready
          </span>
        ) : null}
      </div>

      {/* Body */}
      <div style={{ padding: "20px", display: "flex", flexDirection: "column", flexGrow: 1 }}>
        {/* Case 1: A new file is selected locally */}
        {file ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px", flexGrow: 1, justifyContent: "space-between" }}>
            <div
              style={{
                border: "1.5px solid #FED7AA",
                backgroundColor: "#FFF7ED",
                borderRadius: "12px",
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <div className="d-flex align-items-center" style={{ gap: "12px", minWidth: 0 }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#FFEDD5",
                    color: "#EA580C",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PictureAsPdfIcon style={{ fontSize: "22px" }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p
                    className="m-0 font-weight-bold text-truncate"
                    style={{ color: "#0F172A", fontSize: "14px", maxWidth: "210px" }}
                    title={file.name}
                  >
                    {file.name}
                  </p>
                  <span style={{ color: "#64748B", fontSize: "12px" }}>{formatFileSize(file.size)}</span>
                </div>
              </div>

              {!isUploading && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFileSelect(null);
                    if (inputRef.current) inputRef.current.value = "";
                  }}
                  style={{
                    backgroundColor: "#FEE2E2",
                    color: "#DC2626",
                    borderRadius: "8px",
                    padding: "6px 10px",
                    border: "none",
                  }}
                  title="Remove selected file"
                >
                  <DeleteOutlineIcon style={{ fontSize: "18px" }} />
                </button>
              )}
            </div>

            {uploadedMessage && !isSuccess && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#DC2626",
                  fontWeight: 500,
                  padding: "4px 8px",
                  borderRadius: "6px",
                  backgroundColor: "#FEE2E2",
                }}
              >
                {uploadedMessage}
              </div>
            )}

            {/* Upload Button */}
            <div className="d-flex justify-content-between align-items-center mt-2" style={{ gap: "8px" }}>
              {hasExisting && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-muted p-0"
                  onClick={() => {
                    onFileSelect(null);
                    if (inputRef.current) inputRef.current.value = "";
                  }}
                  style={{ fontSize: "12px", textDecoration: "none" }}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm font-weight-bold d-flex align-items-center ml-auto"
                disabled={isUploading}
                onClick={onUploadSingle}
                style={{
                  backgroundColor: "#EA580C",
                  color: "#FFFFFF",
                  borderRadius: "10px",
                  padding: "8px 18px",
                  fontSize: "13px",
                  gap: "6px",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(234, 88, 12, 0.2)",
                  cursor: isUploading ? "not-allowed" : "pointer",
                }}
              >
                {isUploading ? (
                  <>
                    <CircularProgress size={14} style={{ color: "#FFFFFF" }} />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <CloudUploadIcon style={{ fontSize: "16px" }} />
                    <span>{hasExisting ? "Update / Replace File" : "Upload This File"}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ) : hasExisting ? (
          /* Case 2: Document already exists from GET API */
          <div style={{ display: "flex", flexDirection: "column", gap: "14px", flexGrow: 1, justifyContent: "space-between" }}>
            <div
              style={{
                border: "1px solid #BBF7D0",
                backgroundColor: "#F0FDF4",
                borderRadius: "14px",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <div className="d-flex align-items-center" style={{ gap: "12px", minWidth: 0 }}>
                <div
                  style={{
                    width: "42px",
                    height: "42px",
                    borderRadius: "10px",
                    backgroundColor: "#DCFCE7",
                    color: "#16A34A",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PictureAsPdfIcon style={{ fontSize: "24px" }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p
                    className="m-0 font-weight-bold text-truncate"
                    style={{ color: "#0F172A", fontSize: "14px", maxWidth: "210px" }}
                    title={existingDoc?.name}
                  >
                    {existingDoc?.name}
                  </p>
                  <span style={{ color: "#16A34A", fontSize: "12px", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                    ✓ Uploaded & Active Document
                  </span>
                  {existingDoc?.updatedAt && (
                    <span style={{ color: "#64748B", fontSize: "11px", display: "block", marginTop: "2px" }}>
                      Updated: {formatDocDate(existingDoc.updatedAt)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {uploadedMessage && !isSuccess && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#DC2626",
                  fontWeight: 500,
                  padding: "4px 8px",
                  borderRadius: "6px",
                  backgroundColor: "#FEE2E2",
                }}
              >
                {uploadedMessage}
              </div>
            )}

            {/* Action buttons for existing file: View Document & Replace */}
            <div className="d-flex justify-content-between align-items-center mt-2" style={{ gap: "8px" }}>
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center"
                onClick={() => {
                  if (existingDoc?.url) {
                    window.open(existingDoc.url, "_blank", "noopener,noreferrer");
                  }
                }}
                style={{
                  backgroundColor: "#F1F5F9",
                  color: "#334155",
                  borderRadius: "8px",
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  gap: "6px",
                  border: "1px solid #CBD5E1",
                }}
              >
                <OpenInNewIcon style={{ fontSize: "14px" }} />
                <span>View File</span>
              </button>

              <button
                type="button"
                className="btn btn-sm d-flex align-items-center"
                onClick={() => inputRef.current?.click()}
                style={{
                  backgroundColor: "#FFFFFF",
                  color: "#EA580C",
                  borderRadius: "8px",
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  gap: "6px",
                  border: "1.5px solid #EA580C",
                }}
              >
                <CloudUploadIcon style={{ fontSize: "14px" }} />
                <span>Replace File</span>
              </button>
            </div>
          </div>
        ) : (
          /* Case 3: Empty upload dropzone */
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => inputRef.current?.click()}
            style={{
              border: isDragging ? "2px dashed #EA580C" : "2px dashed #CBD5E1",
              borderRadius: "14px",
              backgroundColor: isDragging ? "#FFF7ED" : "#F8FAFC",
              padding: "36px 16px",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                backgroundColor: "#FFF7ED",
                color: "#EA580C",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "10px",
              }}
            >
              <CloudUploadIcon style={{ fontSize: "26px" }} />
            </div>
            <p className="m-0 font-weight-bold" style={{ color: "#0F172A", fontSize: "14px" }}>
              Click to select or drag & drop PDF
            </p>
            <span style={{ color: "#94A3B8", fontSize: "12px", display: "block", marginTop: "4px" }}>
              Only PDF format allowed (Max: 4MB)
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const TermAndConditions: React.FC = () => {
  const [file1, setFile1] = useState<File | null>(null);
  const [file2, setFile2] = useState<File | null>(null);
  const [file3, setFile3] = useState<File | null>(null);

  const [existing1, setExisting1] = useState<ExistingDoc | null>(null);
  const [existing2, setExisting2] = useState<ExistingDoc | null>(null);
  const [existing3, setExisting3] = useState<ExistingDoc | null>(null);

  const [status1, setStatus1] = useState<"idle" | "uploading" | "success" | "error">("idle");
  const [status2, setStatus2] = useState<"idle" | "uploading" | "success" | "error">("idle");
  const [status3, setStatus3] = useState<"idle" | "uploading" | "success" | "error">("idle");

  const [msg1, setMsg1] = useState<string>("");
  const [msg2, setMsg2] = useState<string>("");
  const [msg3, setMsg3] = useState<string>("");

  const [isFetching, setIsFetching] = useState<boolean>(true);

  // Helper to extract document from API item
  const extractDoc = (item: any, defaultUpdatedAt?: string): ExistingDoc | null => {
    if (!item) return null;
    if (typeof item === "string" && item.trim() !== "") {
      const url = item.trim();
      const rawName = decodeURIComponent(url.split("/").pop()?.split("?")[0] || "Document.pdf");
      // Remove leading timestamp like 1790146690870_Invoice-825776.pdf -> Invoice-825776.pdf
      const name = rawName.replace(/^\d{10,15}[-_]/, "");
      return { url, name, updatedAt: defaultUpdatedAt };
    }
    if (typeof item === "object") {
      const url = item.url || item.file || item.file_url || item.path || item.document_url || item.link || "";
      if (!url || typeof url !== "string" || url.trim() === "") return null;
      const rawName = item.name || item.filename || item.file_name || item.original_name || decodeURIComponent(url.split("/").pop()?.split("?")[0] || "Document.pdf");
      const name = typeof rawName === "string" ? rawName.replace(/^\d{10,15}[-_]/, "") : "Document.pdf";
      const updatedAt = item.updated_at || item.created_at || item.updatedAt || item.date || defaultUpdatedAt || "";
      return { url, name, updatedAt };
    }
    return null;
  };

  // Helper to parse GET API response
  const parseAndSetExistingDocs = (resData: any) => {
    if (!resData) return;
    const payload = resData.data ?? resData.result ?? resData;
    const defaultUpdatedAt = payload?.updated_at || payload?.updatedAt || resData?.updated_at || "";

    // Structure A: Array of documents or array with 1 record
    if (Array.isArray(payload)) {
      payload.forEach((item: any) => {
        if (!item) return;

        // Check if item contains the full object with properties
        const termsVal = item.seller_terms_and_conditions || item["seller-terms-and-conditions"] || item.sellerTermsAndConditions || item.terms;
        const commVal = item.commission_policy || item["commission-policy"] || item.commissionPolicy || item.commission;
        const retVal = item.return_refund_policy || item["return-refund-policy"] || item.returnRefundPolicy || item.refund_policy;

        if (termsVal || commVal || retVal) {
          const itemUpdatedAt = item.updated_at || item.created_at || defaultUpdatedAt;
          if (termsVal) setExisting1(extractDoc(termsVal, itemUpdatedAt));
          if (commVal) setExisting2(extractDoc(commVal, itemUpdatedAt));
          if (retVal) setExisting3(extractDoc(retVal, itemUpdatedAt));
          return;
        }

        const type = (item?.type || item?.name || item?.policy_type || item?.document_type || item?.key || "").toLowerCase();
        const doc = extractDoc(item, item.updated_at || defaultUpdatedAt);
        if (!doc) return;

        if (type.includes("seller") || (type.includes("term") && !type.includes("return"))) {
          setExisting1(doc);
        } else if (type.includes("commission")) {
          setExisting2(doc);
        } else if (type.includes("return") || type.includes("refund")) {
          setExisting3(doc);
        }
      });
      return;
    }

    // Structure B: Object with keys (matches user's API response)
    if (typeof payload === "object" && payload !== null) {
      // 1. Seller Terms & Conditions
      const termsVal =
        payload.seller_terms_and_conditions ||
        payload["seller-terms-and-conditions"] ||
        payload.sellerTermsAndConditions ||
        payload.terms_and_conditions ||
        payload["terms-and-conditions"] ||
        payload.seller_terms ||
        payload.terms;
      if (termsVal) setExisting1(extractDoc(termsVal, defaultUpdatedAt));

      // 2. Commission Policy
      const commVal =
        payload.commission_policy ||
        payload["commission-policy"] ||
        payload.commissionPolicy ||
        payload.commission;
      if (commVal) setExisting2(extractDoc(commVal, defaultUpdatedAt));

      // 3. Return & Refund Policy
      const retVal =
        payload.return_refund_policy ||
        payload["return-refund-policy"] ||
        payload.returnRefundPolicy ||
        payload.return_and_refund_policy ||
        payload.refund_policy ||
        payload.return_policy;
      if (retVal) setExisting3(extractDoc(retVal, defaultUpdatedAt));
    }
  };

  // GET API to fetch all existing terms & conditions documents
  const fetchTermsConditions = useCallback(async () => {
    setIsFetching(true);
    const token = sessionStorage.getItem("token") || "";
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
      headers["x-custom-authorization"] = `Bearer ${token}`;
    }

    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const primaryUrl = isLocal
      ? `http://localhost:4000/digidukaan/snp/terms-conditions/get`
      : `${config.apiEndpoint}/snp/terms-conditions/get`;

    const fallbackUrl = isLocal
      ? `${config.apiEndpoint}/snp/terms-conditions/get`
      : `http://localhost:4000/digidukaan/snp/terms-conditions/get`;

    try {
      let res;
      try {
        res = await axios.get(primaryUrl, { headers });
      } catch (err: any) {
        if (err?.code === "ERR_NETWORK" || !err.response) {
          res = await axios.get(fallbackUrl, { headers });
        } else {
          throw err;
        }
      }

      console.log("Terms & Conditions GET response:", res?.data);
      parseAndSetExistingDocs(res?.data);
    } catch (err) {
      console.log("Failed to fetch existing terms & conditions:", err);
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchTermsConditions();
  }, [fetchTermsConditions]);

  // Helper to execute file upload with automatic host fallback
  const uploadFileToApi = async (file: File, endpointPath: string) => {
    const token = sessionStorage.getItem("token") || "";
    const formData = new FormData();
    formData.append("file", file);

    const headers: Record<string, string> = {
      "Content-Type": "multipart/form-data",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
      headers["x-custom-authorization"] = `Bearer ${token}`;
    }

    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    const primaryUrl = isLocal
      ? `http://localhost:4000/digidukaan/snp/terms-conditions/${endpointPath}`
      : `${config.apiEndpoint}/snp/terms-conditions/${endpointPath}`;

    const fallbackUrl = isLocal
      ? `${config.apiEndpoint}/snp/terms-conditions/${endpointPath}`
      : `http://localhost:4000/digidukaan/snp/terms-conditions/${endpointPath}`;

    try {
      const response = await axios.post(primaryUrl, formData, { headers });
      return response.data;
    } catch (primaryError: any) {
      if (primaryError?.code === "ERR_NETWORK" || !primaryError.response) {
        console.warn(`Primary endpoint ${primaryUrl} unreachable, trying fallback ${fallbackUrl}...`);
        const response = await axios.post(fallbackUrl, formData, { headers });
        return response.data;
      }
      throw primaryError;
    }
  };

  const MAX_UPLOAD_SIZE = 4 * 1024 * 1024; // 4 MB

  // Upload Single File Handlers
  const handleUploadFile1 = async () => {
    if (!file1) {
      toast.warn("Please select Seller Terms & Conditions file first");
      return;
    }
    if (file1.size > MAX_UPLOAD_SIZE) {
      toast.error("File size cannot exceed 4 MB");
      return;
    }
    setStatus1("uploading");
    setMsg1("");
    try {
      const res = await uploadFileToApi(file1, "seller-terms-and-conditions");
      if (res?.status === false) {
        setStatus1("error");
        setMsg1(res?.message || "Upload failed");
        toast.error(res?.message || "Failed to upload Seller Terms & Conditions");
      } else {
        setStatus1("success");
        setMsg1("");
        toast.success(res?.message || "Seller Terms & Conditions uploaded successfully!");
        setFile1(null);
        fetchTermsConditions();
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || "Upload failed";
      setStatus1("error");
      setMsg1(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleUploadFile2 = async () => {
    if (!file2) {
      toast.warn("Please select Commission Policy file first");
      return;
    }
    if (file2.size > MAX_UPLOAD_SIZE) {
      toast.error("File size cannot exceed 4 MB");
      return;
    }
    setStatus2("uploading");
    setMsg2("");
    try {
      const res = await uploadFileToApi(file2, "commission-policy");
      if (res?.status === false) {
        setStatus2("error");
        setMsg2(res?.message || "Upload failed");
        toast.error(res?.message || "Failed to upload Commission Policy");
      } else {
        setStatus2("success");
        setMsg2("");
        toast.success(res?.message || "Commission Policy uploaded successfully!");
        setFile2(null);
        fetchTermsConditions();
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || "Upload failed";
      setStatus2("error");
      setMsg2(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleUploadFile3 = async () => {
    if (!file3) {
      toast.warn("Please select Return & Refund Policy file first");
      return;
    }
    if (file3.size > MAX_UPLOAD_SIZE) {
      toast.error("File size cannot exceed 4 MB");
      return;
    }
    setStatus3("uploading");
    setMsg3("");
    try {
      const res = await uploadFileToApi(file3, "return-refund-policy");
      if (res?.status === false) {
        setStatus3("error");
        setMsg3(res?.message || "Upload failed");
        toast.error(res?.message || "Failed to upload Return & Refund Policy");
      } else {
        setStatus3("success");
        setMsg3("");
        toast.success(res?.message || "Return & Refund Policy uploaded successfully!");
        setFile3(null);
        fetchTermsConditions();
      }
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message || "Upload failed";
      setStatus3("error");
      setMsg3(errorMsg);
      toast.error(errorMsg);
    }
  };



  const handleReset = () => {
    setFile1(null);
    setFile2(null);
    setFile3(null);
    setStatus1("idle");
    setStatus2("idle");
    setStatus3("idle");
    setMsg1("");
    setMsg2("");
    setMsg3("");
  };

  const selectedCount = [file1, file2, file3].filter(Boolean).length;
  const existingCount = [Boolean(existing1?.url), Boolean(existing2?.url), Boolean(existing3?.url)].filter(Boolean).length;

  return (
    <ThemeMaker>
      <div className="container-fluid px-3 py-3" style={{ maxWidth: "1280px", margin: "0 auto" }}>

        {/* Header Banner */}
        <div
          className="card border-0 shadow-sm mb-4"
          style={{
            borderRadius: "20px",
            background: "linear-gradient(135deg, #FFF7ED 0%, #FFFFFF 100%)",
            border: "1px solid #FED7AA",
            padding: "26px",
          }}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between" style={{ gap: "16px" }}>
            <div className="d-flex align-items-center" style={{ gap: "16px" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "16px",
                  backgroundColor: "#EA580C",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 10px 20px rgba(234, 88, 12, 0.22)",
                  flexShrink: 0,
                }}
              >
                <CloudUploadIcon style={{ fontSize: "30px" }} />
              </div>
              <div>
                <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
                  <h3 className="m-0 font-weight-bold" style={{ color: "#0F172A", fontSize: "24px", letterSpacing: "-0.5px" }}>
                    Terms & Policy Document Management
                  </h3>
                  <span
                    style={{
                      backgroundColor: existingCount === 3 ? "#DCFCE7" : existingCount > 0 ? "#FFEDD5" : "#F1F5F9",
                      color: existingCount === 3 ? "#15803D" : existingCount > 0 ? "#C2410C" : "#64748B",
                      fontSize: "12px",
                      fontWeight: 700,
                      padding: "4px 12px",
                      borderRadius: "20px",
                      border: existingCount === 3 ? "1px solid #BBF7D0" : existingCount > 0 ? "1px solid #FDBA74" : "1px solid #E2E8F0",
                    }}
                  >
                    {isFetching ? "Checking..." : `${existingCount} of 3 Documents Active`}
                  </span>
                </div>
                <p className="m-0 mt-1" style={{ color: "#64748B", fontSize: "14px" }}>
                  Manage Seller Terms & Conditions, Commission Policy, and Return & Refund Policy documents.
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              {selectedCount > 0 && (
                <button
                  type="button"
                  className="btn btn-sm font-weight-bold"
                  onClick={handleReset}
                  style={{
                    backgroundColor: "#F1F5F9",
                    color: "#64748B",
                    borderRadius: "10px",
                    padding: "8px 16px",
                    border: "none",
                  }}
                >
                  Clear Selected
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 3 Upload Cards */}
        <div className="row g-4 mb-4">
          {/* File 1: Seller Terms & Conditions */}
          <div className="col-lg-4 col-md-6 mb-3">
            <FileUploadCard
              id="upload_terms_and_conditions"
              title="Seller Terms & Conditions"
              description="Upload Seller Terms & Conditions (PDF)"
              endpointPath="seller-terms-and-conditions"
              icon={DescriptionIcon}
              file={file1}
              existingDoc={existing1}
              uploadedStatus={status1}
              uploadedMessage={msg1}
              onFileSelect={(f) => {
                setFile1(f);
                setStatus1("idle");
                setMsg1("");
              }}
              onUploadSingle={handleUploadFile1}
            />
          </div>

          {/* File 2: Commission Policy */}
          <div className="col-lg-4 col-md-6 mb-3">
            <FileUploadCard
              id="upload_commission_policy"
              title="Commission Policy"
              description="Upload Commission Policy Document (PDF)"
              endpointPath="commission-policy"
              icon={MonetizationOnIcon}
              file={file2}
              existingDoc={existing2}
              uploadedStatus={status2}
              uploadedMessage={msg2}
              onFileSelect={(f) => {
                setFile2(f);
                setStatus2("idle");
                setMsg2("");
              }}
              onUploadSingle={handleUploadFile2}
            />
          </div>

          {/* File 3: Return & Refund Policy */}
          <div className="col-lg-4 col-md-12 mb-3">
            <FileUploadCard
              id="upload_return_refund_policy"
              title="Return & Refund Policy"
              description="Upload Return & Refund Policy (PDF)"
              endpointPath="return-refund-policy"
              icon={AssignmentReturnIcon}
              file={file3}
              existingDoc={existing3}
              uploadedStatus={status3}
              uploadedMessage={msg3}
              onFileSelect={(f) => {
                setFile3(f);
                setStatus3("idle");
                setMsg3("");
              }}
              onUploadSingle={handleUploadFile3}
            />
          </div>
        </div>



      </div>
    </ThemeMaker>
  );
};

export default TermAndConditions;
